import { ReuploadPolicy } from '../types/reuploadPolicy';

interface ApiResponse<T> {
  success?: boolean;
  isSuccess?: boolean;
  data?: T;
  detail?: T;
  message?: string;
}

interface StoredPolicy {
  expiresAt: number;
  policy: ReuploadPolicy;
}

const STORAGE_KEY = 'imgcloud_reupload_policy';
const CACHE_TTL_MS = 60 * 1000;

let cachedPolicy: ReuploadPolicy | null = null;
let inflightRequest: Promise<ReuploadPolicy> | null = null;

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const isValidPolicy = (policy: unknown): policy is ReuploadPolicy => {
  if (!policy || typeof policy !== 'object') {
    return false;
  }

  const value = policy as Partial<ReuploadPolicy>;
  return Array.isArray(value.allowedExtensions);
};

const normalizePolicy = (policy: ReuploadPolicy): ReuploadPolicy => {
  const allowedExtensions = policy.allowedExtensions
    .map(extension => extension.toLowerCase().replace(/^\./, ''))
    .filter(Boolean);

  return {
    allowedExtensions,
    accept: policy.accept || allowedExtensions.map(extension => `.${extension}`).join(','),
    sameExtensionRequired: policy.sameExtensionRequired !== false,
    temporaryAllowed: policy.temporaryAllowed === true,
    version: policy.version || allowedExtensions.join(','),
  };
};

const readStoredPolicy = () => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const stored = JSON.parse(raw) as StoredPolicy;
    if (stored.expiresAt <= Date.now() || !isValidPolicy(stored.policy)) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }

    return normalizePolicy(stored.policy);
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
};

const writeStoredPolicy = (policy: ReuploadPolicy) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        expiresAt: Date.now() + CACHE_TTL_MS,
        policy,
      })
    );
  } catch {
    // localStorage may be unavailable in private mode; in-memory cache still works.
  }
};

export const clearReuploadPolicyCache = () => {
  cachedPolicy = null;
  inflightRequest = null;
  const storage = getStorage();
  if (storage) {
    storage.removeItem(STORAGE_KEY);
  }
};

export const fetchReuploadPolicy = async (): Promise<ReuploadPolicy> => {
  if (cachedPolicy) {
    return cachedPolicy;
  }

  const storedPolicy = readStoredPolicy();
  if (storedPolicy) {
    cachedPolicy = storedPolicy;
    return storedPolicy;
  }

  if (inflightRequest) {
    return inflightRequest;
  }

  inflightRequest = fetch('/img/reupload/policy', {
    method: 'GET',
    credentials: 'include',
  })
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch reupload policy: ${response.status}`);
      }

      const result = (await response.json()) as ApiResponse<ReuploadPolicy>;
      const ok = result.success === true || result.isSuccess === true;
      const policy = result.data || result.detail;

      if (!ok || !isValidPolicy(policy)) {
        throw new Error(result.message || 'Failed to fetch reupload policy');
      }

      const nextPolicy = normalizePolicy(policy);
      cachedPolicy = nextPolicy;
      writeStoredPolicy(nextPolicy);
      return nextPolicy;
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
};
